import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { updatePluginStatusFromCRM, getUserPluginData } from '../../services/userPluginService';
import { getCompanyIdForUser } from '../../services/firestoreService';
import {
  getDefaultEstimateCalculatorConfig,
  deepMergeEstimateConfig
} from '../../data/estimateCalculatorDefaults';
import { buildEstimateCalculatorRbaImportJson } from '../../utils/estimateCalculatorRbaExport';
import EstimateCalculatorBackupViewer from '../../components/crm/EstimateCalculatorBackupViewer';
import { 
  FaArrowLeft, 
  FaPlug, 
  FaCheckCircle, 
  FaClock, 
  FaExclamationTriangle,
  FaCode,
  FaTrash,
  FaCopy,
  FaDownload
} from 'react-icons/fa';
import PortalBackground from '../../components/PortalBackground';
import BBALoader from '../../components/BBALoader';

const PluginDetail = () => {
  const { darkMode } = useTheme();
  const { pluginId } = useParams();
  const navigate = useNavigate();
  const [plugin, setPlugin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [mergedEstimateConfig, setMergedEstimateConfig] = useState(null);
  const [estimateExportLoading, setEstimateExportLoading] = useState(false);
  const [apiKeyCopiedFlash, setApiKeyCopiedFlash] = useState(false);

  const handleCopyApiKey = useCallback(() => {
    if (!plugin?.requestApiKey || !navigator.clipboard?.writeText) return;
    navigator.clipboard.writeText(plugin.requestApiKey).then(() => {
      setApiKeyCopiedFlash(true);
      setTimeout(() => setApiKeyCopiedFlash(false), 2000);
    });
  }, [plugin?.requestApiKey]);

  const handleDownloadEstimateJson = useCallback(() => {
    if (!mergedEstimateConfig) return;
    const payload = buildEstimateCalculatorRbaImportJson(mergedEstimateConfig);
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const safe =
      (plugin?.company || 'client')
        .replace(/[^\w\-]+/g, '-')
        .slice(0, 48) || 'client';
    const a = document.createElement('a');
    a.href = url;
    a.download = `estimate-calculator-import-${safe}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [mergedEstimateConfig, plugin?.company]);

  // Fetch plugin details
  useEffect(() => {
    const fetchPlugin = async () => {
      try {
        const pluginRef = doc(db, 'pluginRequests', pluginId);
        const pluginDoc = await getDoc(pluginRef);
        
        if (pluginDoc.exists()) {
          const data = pluginDoc.data();
          
          // Get user data to fetch contact person name
          let contactPersonName = 'Unknown';
          let requestedByName = 'Unknown';
          
          if (data.userId) {
            try {
              const userDoc = await getDoc(doc(db, 'users', data.userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                contactPersonName = userData.displayName || userData.firstName + ' ' + userData.lastName || userData.email || 'Unknown';
                requestedByName = userData.displayName || userData.firstName + ' ' + userData.lastName || userData.email || 'Unknown';
              }
            } catch (error) {
              console.error('Error fetching user data:', error);
            }
          }

          // Get plugin template details
          let pluginTemplate = null;
          if (data.pluginId) {
            try {
              const templateDoc = await getDoc(doc(db, 'pluginTemplates', data.pluginId));
              if (templateDoc.exists()) {
                pluginTemplate = templateDoc.data();
              }
            } catch (error) {
              console.error('Error fetching plugin template:', error);
            }
          }
          
          const pluginData = {
            id: pluginDoc.id,
            ...data,
            status: data.status || 'requested',
            requestDate: data.requestedAt || new Date().toISOString(),
            contactPersonName,
            requestedByName,
            company: data.company || 'Unknown Company',
            pluginTemplate: pluginTemplate
          };
          setPlugin(pluginData);
          setSelectedStatus(pluginData.status);
        } else {
          console.error('Plugin not found');
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching plugin:', error);
        setLoading(false);
      }
    };

    if (pluginId) {
      fetchPlugin();
    }
  }, [pluginId]);

  useEffect(() => {
    let cancelled = false;

    const loadMergedEstimateConfig = async () => {
      if (!plugin?.userId || !plugin?.pluginId) {
        setMergedEstimateConfig(null);
        setEstimateExportLoading(false);
        return;
      }
      setEstimateExportLoading(true);
      try {
        const accountId = await getCompanyIdForUser(plugin.userId);
        const map = await getUserPluginData(plugin.userId, accountId);
        const defaults = getDefaultEstimateCalculatorConfig();
        const saved = map[plugin.pluginId]?.customizations?.estimateCalculator;
        let merged = deepMergeEstimateConfig(defaults, saved);
        const rk =
          map[plugin.pluginId]?.requestApiKey ||
          (typeof plugin.requestApiKey === 'string' ? plugin.requestApiKey.trim() : '');
        if (rk && !(merged.settings && merged.settings.apiKey)) {
          merged = {
            ...merged,
            settings: {
              ...merged.settings,
              apiKey: rk
            }
          };
        }
        if (!cancelled) setMergedEstimateConfig(merged);
      } catch (e) {
        console.error('Error loading estimate calculator export config:', e);
        if (!cancelled) setMergedEstimateConfig(null);
      } finally {
        if (!cancelled) setEstimateExportLoading(false);
      }
    };

    loadMergedEstimateConfig();
    return () => {
      cancelled = true;
    };
  }, [plugin?.userId, plugin?.pluginId, plugin?.requestApiKey]);

  // Get status badge styling
  const getStatusBadge = (status) => {
    const statusConfig = {
      'requested': { bg: 'bg-red-100 text-red-800', icon: <FaExclamationTriangle className="w-3 h-3" />, text: 'Requested' },
      'in-progress': { bg: 'bg-yellow-100 text-yellow-800', icon: <FaCode className="w-3 h-3" />, text: 'In Progress' },
      'active': { bg: 'bg-green-100 text-green-800', icon: <FaCheckCircle className="w-3 h-3" />, text: 'Active' },
      'off': { bg: 'bg-gray-100 text-gray-800', icon: <FaClock className="w-3 h-3" />, text: 'Off' },
      'approved': { bg: 'bg-blue-100 text-blue-800', icon: <FaCheckCircle className="w-3 h-3" />, text: 'Approved' },
      'denied': { bg: 'bg-red-100 text-red-800', icon: <FaExclamationTriangle className="w-3 h-3" />, text: 'Denied' }
    };

    const config = statusConfig[status] || statusConfig['requested'];
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg}`}>
        {config.icon}
        <span className="ml-1">{config.text}</span>
      </span>
    );
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    // Handle Firestore timestamp objects
    if (dateString && typeof dateString === 'object' && dateString.seconds) {
      const date = new Date(dateString.seconds * 1000);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    
    // Handle regular date strings
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  // Handle status selection
  const handleStatusSelect = (status) => {
    setSelectedStatus(status);
    setShowStatusDropdown(false);
  };

  // Handle save status
  const handleSaveStatus = async () => {
    if (!plugin || saving || !selectedStatus || selectedStatus === plugin.status) return;
    
    setSaving(true);
    try {
      // Update plugin status using CRM service
      await updatePluginStatusFromCRM(pluginId, selectedStatus);
      
      // Update local state
      setPlugin(prev => ({
        ...prev,
        status: selectedStatus
      }));
      
    } catch (error) {
      console.error('Error updating plugin status:', error);
    } finally {
      setSaving(false);
    }
  };

  // Handle delete plugin request
  const handleDeleteRequest = async () => {
    if (!plugin || deleting) return;
    
    const confirmed = window.confirm(
      'Are you sure you want to delete this plugin request? This will only delete the request, not the plugin template.'
    );
    
    if (!confirmed) return;
    
    setDeleting(true);
    try {
      // Delete the plugin request document
      await deleteDoc(doc(db, 'pluginRequests', pluginId));
      
      // Navigate back to the marketing fulfillment plugins tab
      navigate('/crm/fulfillment/marketing', { state: { activeTab: 'plugins' } });
      
    } catch (error) {
      console.error('Error deleting plugin request:', error);
      alert('Failed to delete plugin request. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center relative">
        <PortalBackground />
        <div className="relative text-center">
          <BBALoader size={48} className="mx-auto" />
          <p className={`mt-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading plugin details...</p>
        </div>
      </div>
    );
  }

  if (!plugin) {
    return (
      <div className="min-h-screen flex items-center justify-center relative">
        <PortalBackground />
        <div className="relative text-center">
          <FaPlug className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Plugin Not Found</h3>
          <p className="text-gray-500 dark:text-gray-400">The requested plugin could not be found.</p>
          <button
            onClick={() => navigate('/crm/fulfillment/marketing', { state: { activeTab: 'plugins' } })}
            className="mt-4 px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600"
          >
            Back to Marketing Fulfillment
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-center relative mb-8">
          <button
            onClick={() => navigate('/crm/fulfillment/marketing', { state: { activeTab: 'plugins' } })}
            className={`absolute left-0 p-2 rounded-lg ${darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-600 hover:bg-gray-100'} shadow-md`}
          >
            <FaArrowLeft className="w-5 h-5" />
          </button>
          <div className="text-center">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
              {plugin.pluginName || 'Untitled Plugin'}
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              Marketing Fulfillment • {plugin.company}
            </p>
          </div>
          
          {/* Action Buttons */}
          <div className="absolute right-0 flex items-center space-x-2">
            {/* Save Button */}
            {selectedStatus && selectedStatus !== plugin.status && (
              <button
                onClick={handleSaveStatus}
                disabled={saving}
                className={`px-4 py-2 rounded-lg flex items-center space-x-2 ${
                  saving 
                    ? 'bg-gray-400 text-white cursor-not-allowed' 
                    : 'bg-green-500 text-white hover:bg-green-600'
                } shadow-md`}
              >
                <FaCheckCircle className="w-4 h-4" />
                <span>{saving ? 'Saving...' : 'Save'}</span>
              </button>
            )}
            
            {/* Delete Button */}
            <button
              onClick={handleDeleteRequest}
              disabled={deleting}
              className={`p-2 rounded-lg flex items-center ${
                deleting 
                  ? 'bg-gray-400 text-white cursor-not-allowed' 
                  : 'bg-red-500 text-white hover:bg-red-600'
              } shadow-md`}
              title="Delete Plugin Request"
            >
              <FaTrash className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information Card */}
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 text-center">Basic Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                    Status
                  </label>
                  <div className="flex items-center space-x-2">
                    {getStatusBadge(plugin.status)}
                  </div>
                </div>
                
                <div>
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                    Plugin Type
                  </label>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} capitalize`}>
                    Website Calculator
                  </p>
                </div>
                
                <div>
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                    Requested By
                  </label>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {plugin.requestedByName}
                  </p>
                </div>
                
                <div>
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                    Request Date
                  </label>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {formatDate(plugin.requestDate)}
                  </p>
                </div>
              </div>
              
              {plugin.requestApiKey && (
                <div className="mt-4 p-4 rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20">
                  <label className={`block text-sm font-medium ${darkMode ? 'text-amber-200' : 'text-amber-900'} mb-2`}>
                    Client API key (for integration)
                  </label>
                  <p className={`text-xs mb-3 ${darkMode ? 'text-amber-100/90' : 'text-amber-900/80'}`}>
                    The key is hidden on screen. Use Copy to paste it where needed.
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    <div
                      className={`flex-1 min-w-[200px] rounded border px-3 py-2 text-sm tracking-widest select-none ${
                        darkMode ? 'bg-gray-900 border-gray-600 text-gray-400' : 'bg-white border-gray-200 text-gray-500'
                      }`}
                      aria-hidden
                    >
                      ••••••••••••••••••••
                    </div>
                    <button
                      type="button"
                      onClick={handleCopyApiKey}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-amber-600 hover:bg-amber-700 text-white"
                      title="Copy API key to clipboard"
                    >
                      <FaCopy className="w-4 h-4" />
                      Copy key
                    </button>
                    {apiKeyCopiedFlash && (
                      <span className="text-sm text-green-600 dark:text-green-400">Copied.</span>
                    )}
                  </div>
                </div>
              )}

              <div className={`mt-6 pt-6 border-t ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Configuration JSON
                </h3>
                <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Download the client&apos;s saved calculator settings as a single JSON object for the WordPress importer
                  ({`rba_*`} keys). Merge defaults apply where the client left fields unchanged.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={handleDownloadEstimateJson}
                    disabled={estimateExportLoading || !mergedEstimateConfig}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white ${
                      estimateExportLoading || !mergedEstimateConfig
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-purple-600 hover:bg-purple-700'
                    }`}
                  >
                    <FaDownload className="w-4 h-4" />
                    {estimateExportLoading ? 'Loading…' : 'Download JSON'}
                  </button>
                  {!estimateExportLoading && mergedEstimateConfig && (
                    <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      Includes webhook URL, completion copy, questions, routing (Q3–Q6), formulas, and API key when saved.
                    </span>
                  )}
                </div>
              </div>
            </div>

          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Company Information Card */}
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center">Company Information</h3>
              <div className="space-y-3">
                <div>
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                    Company
                  </label>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {plugin.company}
                  </p>
                </div>
                
                <div>
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                    Contact Person
                  </label>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {plugin.contactPersonName}
                  </p>
                </div>
              </div>
            </div>

            {/* Update Status Card */}
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center">Update Status</h3>
              <div className="space-y-3">
                {/* Current Status Display */}
                <div className="text-center mb-4">
                  <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Current Status:
                  </p>
                  {getStatusBadge(selectedStatus || plugin.status)}
                </div>

                {/* Status Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                    className={`w-full px-4 py-2 text-left rounded-md border ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600' 
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    } focus:outline-none focus:ring-2 focus:ring-purple-500`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="flex items-center">
                        {getStatusBadge(selectedStatus || plugin.status)}
                      </span>
                      <svg className={`w-4 h-4 transition-transform ${showStatusDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  {showStatusDropdown && (
                    <div className={`absolute z-10 w-full mt-1 rounded-md shadow-lg ${
                      darkMode ? 'bg-gray-700 border border-gray-600' : 'bg-white border border-gray-300'
                    }`}>
                      <div className="py-1">
                        {['requested', 'in-progress', 'active'].map((status) => (
                          <button
                            key={status}
                            onClick={() => handleStatusSelect(status)}
                            className={`w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-600 ${
                              selectedStatus === status ? 'bg-gray-100 dark:bg-gray-600' : ''
                            }`}
                          >
                            <div className="flex items-center">
                              {getStatusBadge(status)}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Status Change Indicator */}
                {selectedStatus && selectedStatus !== plugin.status && (
                  <div className={`text-center p-3 rounded-md ${
                    darkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-50 text-blue-800'
                  }`}>
                    <p className="text-sm">
                      Status will change from <strong>{plugin.status}</strong> to <strong>{selectedStatus}</strong>
                    </p>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>

        <EstimateCalculatorBackupViewer
          darkMode={darkMode}
          loading={estimateExportLoading}
          config={mergedEstimateConfig}
        />

      </div>
    </div>
  );
};

export default PluginDetail;